import { unByKey } from 'ol/Observable';
import { transform } from 'ol/proj';

import { MapMessageDispatcher } from 'cadenza/map/custom/map-message-dispatcher/map-message-dispatcher';
import { LayerProperty } from 'cadenza/map/layer-property';
import { MapProperty } from 'cadenza/map/map-property';
import { context as mapContext } from 'cadenza/map/context/context';
import { getSelectedLayerFeaturesAsPostMessage } from 'cadenza/api-client/maps-api';
import { on } from 'cadenza/utils/event-util';
import { logger } from 'cadenza/utils/logging';
import { CustomAppMessageType } from 'cadenza/map/custom/custom-app-message-type';
import { WGS_84_EPSG_CODE } from 'cadenza/map/custom/map-constants';
import { classicPostMessage } from 'cadenza/integration/classic-post-message';

export class MapClientEventForwarder {

  _map;
  _mapActionService;
  _shouldUseMapSrs;

  constructor (map, mapActionService, shouldUseMapSrs) {
    this._map = map;
    this._mapActionService = mapActionService;
    this._shouldUseMapSrs = shouldUseMapSrs;
  }

  /**
   * Adds postMessage listeners on the Cadenza side for events coming from the businessApp.
   * Sends the INIT event to the businessApp signalling that we're ready to receive commands.
   *
   * @param {string[]} supportedIncomingEventTypes
   */
  connectApiToBusinessApp (supportedIncomingEventTypes) {
    const mapMessageDispatcher = new MapMessageDispatcher(this._map, this._mapActionService);
    classicPostMessage.registerEventListener(event => mapMessageDispatcher.dispatch(event), supportedIncomingEventTypes);
    classicPostMessage.sendEvent({ type: CustomAppMessageType.INIT });
  }

  addListenerToForwardSelectedFeaturesToBusinessApp () {
    let selectedLayerFeaturesEventKey = this._addFeaturesSelectedListener();

    this._map.on(`change:${MapProperty.SELECTED_LAYER}`, () => {
      unByKey(selectedLayerFeaturesEventKey);
      selectedLayerFeaturesEventKey = this._addFeaturesSelectedListener();
    });
  }

  _addFeaturesSelectedListener () {
    const selectedLayer = this._map.get(MapProperty.SELECTED_LAYER);
    if (selectedLayer) {
      return selectedLayer.on(`change:${LayerProperty.SELECTED_FEATURE_IDS}`, () => this._onSelectedFeaturesChange(selectedLayer));
    }
  }

  _onSelectedFeaturesChange (selectedLayer) {
    const selectedLayerId = selectedLayer.get(LayerProperty.ID);
    const srsCode = this._map.getView().getProjection().getCode();
    const mapScale = mapContext(this._map).mapScale;
    const layerName = selectedLayer.get(LayerProperty.NAME);

    getSelectedLayerFeaturesAsPostMessage(selectedLayerId, srsCode, mapScale)
      .then(({ ids, compoundIds }) => {
        classicPostMessage.sendEvent({
          type: CustomAppMessageType.OUTGOING.SELECTION_CHANGED,
          layerName,
          ids,
          compoundIds
        });
      }).catch(error => logger.error('Failed to fetch selected features', error));
  }

  addListenerToForwardGazetteerAndReverseGeocodingResultsToBusinessApp () {
    on(this._map.getTargetElement(), CustomAppMessageType.OUTGOING.LOCATION_SELECTED, event => {
      const mapSrs = this._map.getView().getProjection().getCode();
      classicPostMessage.sendEvent({
        type: event.type,
        ...mapToPublishedResponse(this._shouldUseMapSrs, mapSrs, event.detail)
      });
    });
  }

}

// @Published
function mapToPublishedResponse (shouldUseMapSrs, mapSrs, { address, coordinate, srsCode, searchProviderId, origin } = {}) {
  const destSrsCode = shouldUseMapSrs ? mapSrs : WGS_84_EPSG_CODE;
  coordinate = transform(coordinate, srsCode, destSrsCode);

  const publishedAddress = address ? {
    countryCode: address.countryCode,
    postalCode: address.postalCode,
    municipality: address.municipality,
    cityDistrict: address.cityDistrict,
    street: address.street,
    houseNumber: address.houseNumber
  } : undefined; // undefined (not null), so that is not sent as part of JSON

  return {
    address: publishedAddress,
    origin,
    coordinate,
    srsCode: destSrsCode,
    searchProviderId
  };
}
